import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
    Background,
    Controls,
    Edge,
    Node,
    NodeChange,
    EdgeChange,
    Connection,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Handle,
    Position,
    MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { createGlobalStyle } from 'styled-components';

import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
    SpeedDial,
    SpeedDialIcon,
    SpeedDialAction,
    Stack,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    QuestionMark as QuestionIcon,
    Check as CheckIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    ContentCopy as ContentCopyIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { flowService } from '../services/flowService';
import { QuestionForm, ConclusionForm } from '../components/QuestionEditor';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import api from '../services/api';

const GlobalStyle = createGlobalStyle`
    .react-flow__edge {
        z-index: 1 !important;
        pointer-events: all;
        &:hover {
            cursor: pointer;
    }
        &.selected {
            z-index: 1 !important;
        }
    }
    .react-flow__edge-path {
        stroke-width: 2;
        z-index: 1 !important;
    }
    .react-flow__edge-interaction {
        stroke-width: 4;
        opacity: 0;
        stroke: transparent;
        fill: none;
    }
    .react-flow__node {
        z-index: 5 !important;
    }
    .react-flow__handle {
        z-index: 6;
    }
    .react-flow__connection-path {
        z-index: 1;
    }
    .react-flow__connection {
        z-index: 1;
    }
    .react-flow__connection-line {
        z-index: 1;
    }
    .react-flow-container .react-flow__edge {
        z-index: 1 !important;
    }
    .react-flow-container .react-flow__edge-path {
        z-index: 1 !important;
    }
    .react-flow-container .react-flow__connection {
        z-index: 1 !important;
    }
    
    /* Estilos para garantir que os modais sejam exibidos corretamente em modo de tela cheia */
    .MuiDialog-root {
        z-index: 9999999 !important;
    }
    
    .MuiDialog-paper {
        z-index: 9999999 !important;
    }
    
    .MuiBackdrop-root {
        z-index: 9999998 !important;
    }
    
    /* Quando em modo de tela cheia, garantir que os modais sejam visíveis */
    :fullscreen .MuiDialog-root,
    :-webkit-full-screen .MuiDialog-root,
    :-moz-full-screen .MuiDialog-root,
    :-ms-fullscreen .MuiDialog-root {
        z-index: 9999999 !important;
        position: fixed !important;
        inset: 0 !important;
    }
    
    /* Classe para o wrapper do modal */
    .modal-wrapper {
        position: relative;
        z-index: 9999999 !important;
    }
    
    /* Melhorar a visualização no modo escuro */
    .dark-mode {
        .react-flow__node {
            color: rgba(255, 255, 255, 0.9);
            
            .node-title {
                color: rgba(255, 255, 255, 0.9);
            }
            
            .node-content {
                color: rgba(255, 255, 255, 0.7);
            }
            
            .MuiTypography-root {
                color: rgba(255, 255, 255, 0.9);
                text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);
            }
            
            .MuiPaper-root {
                background-color: rgba(30, 30, 30, 0.7);
                border-color: rgba(255, 255, 255, 0.2);
            }
        }
        
        .react-flow__background {
            background-color: #121212;
        }
    }
`;

// Componente customizado para nó de questão
const QuestionNode = ({ data, selected }) => {
    const theme = useTheme();
    
    // Função auxiliar para comparar IDs
    const isFirstQuestion = () => {
        const currentId = data.id?.toString();
        const firstQuestionId = data.flow_first_question_id?.toString();
        return currentId === firstQuestionId;
    };
    
    // Determina a cor de fundo com base no tipo de conclusão
    const getBackgroundColor = () => {
        if (!data.is_final) {
            // Aumentar o contraste no modo escuro
            return theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.9) // Mais opaco no modo escuro
                : theme.palette.background.paper;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.success.main, 0.1);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.error.main, 0.1);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.info.main, 0.1);
        }
    };
    
    // Determina a cor da borda com base no tipo de conclusão
    const getBorderColor = () => {
        if (selected) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.8) // Borda mais visível quando selecionado no modo escuro
                : theme.palette.primary.main;
        }
        
        if (!data.is_final) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.divider, 0.7) // Borda mais visível no modo escuro
                : theme.palette.divider;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.success.main, 0.3);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.error.main, 0.3);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.info.main, 0.3);
        }
    };
    
    // Determina a cor do texto do tipo de nó
    const getTypeColor = () => {
        if (!data.is_final) {
            return theme.palette.text.secondary;
        }
        
        if (data.is_success === true) {
            return theme.palette.success.main;
        } else if (data.is_success === false) {
            return theme.palette.error.main;
        } else {
            return theme.palette.info.main;
        }
    };
    
    // Determina o ícone do tipo de conclusão
    const getTypeIcon = () => {
        if (!data.is_final) {
            return null;
        }
        
        if (data.is_success === true) {
            return <CheckCircleIcon fontSize="small" sx={{ ml: 1 }} />;
        } else if (data.is_success === false) {
            return <CancelIcon fontSize="small" sx={{ ml: 1 }} />;
        }
        
        return null;
    };
    
    return (
        <Box sx={{ position: 'relative' }}>
            {/* Indicador de primeira questão */}
            {isFirstQuestion() && (
                <Box
                        sx={{
                            position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                        boxShadow: theme.shadows[2],
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />
                    Primeira Questão
                </Box>
            )}
            <Paper
                sx={{
                    minWidth: 300,
                    maxWidth: 400,
                    backgroundColor: getBackgroundColor(),
                    border: '1px solid',
                    borderColor: getBorderColor(),
                    borderRadius: 2,
                    position: 'relative',
                    boxShadow: selected 
                        ? `0 0 0 2px ${theme.palette.primary.main}`
                        : theme.shadows[2],
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                    },
                    zIndex: 0,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ p: 3 }}>
                {/* Handle de entrada no topo */}
                <Handle
                    type="target"
                    position={Position.Top}
                    style={{ 
                        background: theme.palette.primary.main,
                            width: 12,
                            height: 12,
                            top: -6,
                            zIndex: 999999,
                            border: `2px solid ${theme.palette.background.paper}`,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                    }}
                />

                {/* Cabeçalho com tipo e referência */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                        mb: 2,
                        gap: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                                color: getTypeColor(),
                                    fontWeight: 600,
                                    px: 1.5,
                                py: 0.5,
                                backgroundColor: alpha(getTypeColor(), 0.1),
                                    borderRadius: 2,
                                    fontSize: '0.95rem',
                                display: 'flex',
                                    alignItems: 'center',
                                    boxShadow: `0 0 0 1px ${alpha(getTypeColor(), 0.2)}`
                            }}
                        >
                            {data.is_final ? 'Conclusão' : 'Pergunta'}
                            {getTypeIcon()}
                        </Typography>
                    </Box>
                    {data.article_reference && (
                        <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                                color: theme.palette.text.secondary,
                                    fontSize: '0.95rem',
                                    px: 1.5,
                                py: 0.5,
                                    backgroundColor: alpha(theme.palette.background.default, 0.7),
                                    borderRadius: 2,
                                    maxWidth: '60%',
                                    boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.3)}`,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                            }}
                        >
                            Ref: {data.article_reference}
                        </Typography>
                    )}
                </Box>

                {/* Texto principal */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        fontWeight: 500,
                        color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.95)' 
                            : theme.palette.text.primary,
                        mb: 3,
                        lineHeight: 1.5,
                        fontSize: '1.1rem',
                        textShadow: theme.palette.mode === 'dark' 
                            ? '0px 1px 2px rgba(0, 0, 0, 0.5)' 
                            : 'none'
                    }}
                >
                    {data.text}
                </Typography>
                
                {/* Opções e edges */}
                {!data.is_final && data.options && (
                    <Box sx={{ mt: 2 }}>
                        <Box 
                            sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                position: 'relative',
                                zIndex: 0
                            }}
                        >
                                    {(() => {
                                        // Garante que options seja um array
                                        let options = data.options;
                                        if (typeof options === 'string') {
                                            try {
                                                options = JSON.parse(options);
                                            } catch (e) {
                                                console.error('Erro ao parsear options:', e);
                                                options = [];
                                            }
                                        }
                                        // Se não for um array, converte para array vazio
                                        if (!Array.isArray(options)) {
                                            console.warn('options não é um array:', options);
                                            options = [];
                                        }
                                        
                                        return options.map((option, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        position: 'relative',
                                        pb: 3,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            p: 1.5,
                                            width: '100%',
                                            textAlign: 'center',
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.primary.main, 0.2),
                                                borderRadius: 2,
                                            position: 'relative',
                                            transition: 'all 0.2s ease',
                                                boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                            '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                                            },
                                            zIndex: 0
                                        }}
                                    >
                                        <Typography 
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                    fontWeight: 600,
                                                    fontSize: '1rem'
                                            }}
                                        >
                                            {option}
                                        </Typography>
                                    </Paper>
                                    
                                    {/* Handle de saída para cada opção */}
                                    <Handle
                                        type="source"
                                        position={Position.Bottom}
                                        id={index.toString()}
                                        style={{
                                            background: theme.palette.primary.main,
                                                width: 12,
                                                height: 12,
                                            bottom: 0,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                                zIndex: 999999,
                                                border: `2px solid ${theme.palette.background.paper}`,
                                                borderRadius: '50%',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    width: 14,
                                                    height: 14,
                                                    bottom: -1,
                                                    boxShadow: `0 0 8px ${theme.palette.primary.main}`
                                                }
                                        }}
                                    />
                                </Box>
                                        ));
                                    })()}
                        </Box>
                    </Box>
                )}

                {/* Mensagem final para conclusões */}
                {data.is_final && data.final_message && (
                    <Box sx={{ mt: 2 }}>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                fontStyle: 'italic',
                                backgroundColor: alpha(
                                    data.is_success === true 
                                        ? theme.palette.success.main 
                                        : data.is_success === false 
                                            ? theme.palette.error.main 
                                            : theme.palette.info.main, 
                                        0.08
                                ),
                                p: 2,
                                    borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha(
                                    data.is_success === true 
                                        ? theme.palette.success.main 
                                        : data.is_success === false 
                                            ? theme.palette.error.main 
                                            : theme.palette.info.main, 
                                    0.2
                                    ),
                                    boxShadow: `0 2px 4px ${alpha(
                                        data.is_success === true 
                                            ? theme.palette.success.main 
                                            : data.is_success === false 
                                                ? theme.palette.error.main 
                                                : theme.palette.info.main,
                                        0.1
                                    )}`,
                                    lineHeight: 1.6,
                                    fontSize: '1.05rem'
                            }}
                        >
                            {data.final_message}
                        </Typography>
                    </Box>
                )}
                </Box>
            </Paper>
        </Box>
    );
};

// Definindo nodeTypes fora do componente para evitar re-renderizações
const nodeTypes = {
    question: QuestionNode,
};

// Adicionar a função de cor do nó para o MiniMap antes do componente FlowEditor
const getNodeColor = (node) => {
    const theme = node.data?.theme;
    if (node.data?.is_final) {
        if (node.data.is_success === true) {
            return theme?.palette.success.main || '#2E7D32';
        } else if (node.data.is_success === false) {
            return theme?.palette.error.main || '#C62828';
        }
        return theme?.palette.info.main || '#1565C0';
    }
    return theme?.palette.primary.main || '#1976d2';
};

export const FlowEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showSuccess, showError, showWarning } = useNotification();
    const theme = useTheme();

    const [flow, setFlow] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [isQuestionFormOpen, setIsQuestionFormOpen] = useState(false);
    const [isConclusionFormOpen, setIsConclusionFormOpen] = useState(false);
    const [isNewQuestion, setIsNewQuestion] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDraft, setIsDraft] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [selectedNodes, setSelectedNodes] = useState(new Set());
    const [history, setHistory] = useState([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);

    // Função auxiliar para verificar o next_question_logic
    const verifyNextQuestionLogic = (question) => {
        console.log('Verificando next_question_logic:', {
            id: question.id,
            text: question.text,
            next_question_logic: question.next_question_logic
        });

        if (!question.next_question_logic || typeof question.next_question_logic !== 'object') {
            console.warn('next_question_logic inválido:', question.next_question_logic);
            return {};
        }

        // Converte para objeto caso seja string JSON
        if (typeof question.next_question_logic === 'string') {
            try {
                question.next_question_logic = JSON.parse(question.next_question_logic);
            } catch (e) {
                console.error('Erro ao parsear next_question_logic:', e);
                return {};
            }
        }

        return question.next_question_logic;
    };

    // Função auxiliar para corrigir o next_question_logic
    const fixNextQuestionLogic = (questions) => {
        console.log('Corrigindo next_question_logic para', questions.length, 'questões');
        
        // Cria um mapa de IDs originais para IDs atuais
        const originalToCurrentMap = {};
        questions.forEach(question => {
            if (question.original_id) {
                originalToCurrentMap[question.original_id] = question.id;
            }
        });
        
        console.log('Mapa de IDs originais para atuais:', originalToCurrentMap);
        
        // Corrige o next_question_logic de cada questão
        return questions.map(question => {
            // Se a questão não tem next_question_logic, retorna a questão sem alterações
            if (!question.next_question_logic) {
                return question;
            }
            
            // Converte para objeto caso seja string JSON
            let nextQuestionLogic = question.next_question_logic;
            if (typeof nextQuestionLogic === 'string') {
                try {
                    nextQuestionLogic = JSON.parse(nextQuestionLogic);
                } catch (e) {
                    console.error('Erro ao parsear next_question_logic:', e);
                    nextQuestionLogic = {};
                }
            }
            
            // Corrige os IDs no next_question_logic
            const correctedLogic = {};
            Object.entries(nextQuestionLogic).forEach(([key, value]) => {
                // Se o valor existe no mapeamento, usa o ID atual
                correctedLogic[key] = originalToCurrentMap[value] || value;
            });
            
            console.log(`next_question_logic corrigido para questão ${question.id}:`, {
                antigo: nextQuestionLogic,
                novo: correctedLogic
            });
            
            return {
                ...question,
                next_question_logic: correctedLogic
            };
        });
    };

    // Inicializar nós e arestas
    const initializeNodesAndEdges = useCallback((questions) => {
        console.log('Questões recebidas para inicialização:', questions);
        
        if (!questions || questions.length === 0) {
            console.warn('Nenhuma questão recebida para inicialização');
            setNodes([]);
            setEdges([]);
            return;
        }
        
        // Corrigimos o next_question_logic antes de criar os nós e edges
        const correctedQuestions = fixNextQuestionLogic([...questions]);
        console.log('Questões corrigidas:', correctedQuestions);

        // Primeiro, criamos os nós
        const newNodes = correctedQuestions.map((question) => {
            // Garante que next_question_logic seja um objeto
            let validNextQuestionLogic = question.next_question_logic;
            if (typeof validNextQuestionLogic === 'string') {
                try {
                    validNextQuestionLogic = JSON.parse(validNextQuestionLogic);
                } catch (e) {
                    console.warn(`Erro ao parsear next_question_logic da questão ${question.id}:`, e);
                    validNextQuestionLogic = {};
                }
            }
            
            // Se next_question_logic for null ou undefined, inicializa como objeto vazio
            validNextQuestionLogic = validNextQuestionLogic || {};
            
            console.log(`Processando questão ${question.id} (${question.text}) com next_question_logic:`, validNextQuestionLogic);
            
            return {
                id: question.id.toString(),
                type: 'question',
                position: { 
                    x: question.position_x || Math.random() * 500, 
                    y: question.position_y || Math.random() * 500 
                },
                data: {
                    ...question,
                    next_question_logic: validNextQuestionLogic,
                    original_id: question.original_id || question.id,
                    flow_first_question_id: flow?.first_question_id?.toString(),
                    onSetAsFirstQuestion: handleSetAsFirstQuestion,
                    onDelete: handleDeleteQuestion
                },
            };
        });

        console.log('Nós criados:', newNodes);

        // Se não houver uma primeira questão definida, define a questão mais ao topo
        updateFirstQuestionBasedOnPosition(newNodes);

        // Depois, criamos as edges baseadas no next_question_logic de cada nó
        const newEdges = [];
        correctedQuestions.forEach((question) => {
            if (question.is_final) {
                console.log(`Questão ${question.id} é final, não processando conexões`);
                return;
            }
            
            console.log(`Processando conexões para questão ${question.id} (${question.text}):`);
            
            // Garante que next_question_logic seja um objeto
            let validNextQuestionLogic = question.next_question_logic;
            if (typeof validNextQuestionLogic === 'string') {
                try {
                    validNextQuestionLogic = JSON.parse(validNextQuestionLogic);
                } catch (e) {
                    console.warn(`Erro ao parsear next_question_logic da questão ${question.id}:`, e);
                    validNextQuestionLogic = {};
                }
            }
            
            // Se next_question_logic for null ou undefined, inicializa como objeto vazio
            validNextQuestionLogic = validNextQuestionLogic || {};
            
            // Para cada opção na questão
            if (question.options && question.options.length > 0) {
                let options = question.options;
                
                // Converte options para array se for string JSON
                if (typeof options === 'string') {
                    try {
                        options = JSON.parse(options);
                    } catch (e) {
                        console.error(`Erro ao parsear options da questão ${question.id}:`, e);
                        options = [];
                    }
                }
                
                options.forEach((option, index) => {
                    const nextId = validNextQuestionLogic[index];
                    console.log(`Verificando opção "${option}" (${index}): nextId=${nextId}`);
                    
                    if (nextId !== null && nextId !== undefined) {
                        // Verifica se a questão destino existe
                        const targetQuestion = correctedQuestions.find(q => 
                            q.id.toString() === nextId.toString() || 
                            (q.original_id && q.original_id.toString() === nextId.toString())
                        );
                        
                        if (targetQuestion) {
                            console.log(`Criando conexão: ${question.id} (${option}) -> ${targetQuestion.id} (${targetQuestion.text})`);
                            const edge = {
                                id: `${question.id}-${index}-${targetQuestion.id}`,
                                source: question.id.toString(),
                                target: targetQuestion.id.toString(),
                                sourceHandle: index.toString(),
                                label: option,
                                type: 'smoothstep',
                                animated: true,
                                style: { strokeWidth: 2, zIndex: 999999 }
                            };
                            newEdges.push(edge);
                        } else {
                            console.warn(`Questão destino ${nextId} não encontrada para opção "${option}"`);
                        }
                    } else {
                        console.log(`Opção "${option}" não tem conexão definida`);
                    }
                });
            } else {
                console.warn(`Questão ${question.id} não tem opções definidas`);
            }
        });

        console.log('Nós finais:', newNodes);
        console.log('Edges finais:', newEdges);

        setNodes(newNodes);
        setEdges(newEdges);
    }, [flow?.first_question_id]);

    // Handler para definir a primeira questão
    const handleSetAsFirstQuestion = useCallback((questionId) => {
        console.log(`Definindo questão ${questionId} como primeira questão`);
        
        // Atualiza o estado do fluxo
        setFlow(prevFlow => ({
            ...prevFlow,
            first_question_id: parseInt(questionId)
        }));
        
        // Atualiza os nós para refletir a mudança
        setNodes(prevNodes => prevNodes.map(node => ({
            ...node,
            data: {
                ...node.data,
                flow_first_question_id: questionId
            }
        })));
    }, []);

    // Handler para excluir uma questão
    const handleDeleteQuestion = useCallback((questionId) => {
        try {
            console.log(`Excluindo questão ${questionId}`);
            
            // Confirma a exclusão
            if (!window.confirm('Tem certeza que deseja excluir esta questão?')) {
            return;
        }

            // Armazena o estado atual para possível reversão
            const prevNodes = [...nodes];
            const prevEdges = [...edges];
            const prevFlow = {...flow};

            // Remove o nó
            setNodes(nds => nds.filter(node => node.id !== questionId));

            // Remove todas as edges conectadas a este nó
            setEdges(eds => eds.filter(edge => 
                edge.source !== questionId && edge.target !== questionId
            ));
            
            // Atualiza o fluxo
            setFlow(prevFlow => {
                // Remove a questão da lista de questões
                const updatedQuestions = prevFlow.questions.filter(q => 
                    q.id.toString() !== questionId.toString()
                );

                const updatedFlow = {
                    ...prevFlow,
                    questions: updatedQuestions,
                    // Se a questão excluída era a primeira questão, limpa o first_question_id
                    first_question_id: prevFlow.first_question_id?.toString() === questionId.toString() 
                        ? null 
                        : prevFlow.first_question_id
                };

                // Salva as alterações no backend
                flowService.update(Number(id), updatedFlow)
                    .then(() => {
                        showSuccess('Questão excluída com sucesso!');
                    })
                    .catch(error => {
                        console.error('Erro ao excluir questão:', error);
                        showError('Erro ao excluir a questão. Por favor, tente novamente.');
                        
                        // Reverte as alterações locais em caso de erro
                        setEdges(edges);
                        setNodes(nodes);
                        setFlow(prevFlow);
                    });

                return updatedFlow;
            });

        } catch (error) {
            console.error('Erro ao excluir questão:', error);
            showError('Erro ao excluir a questão. Por favor, tente novamente.');
        }
    }, [edges, nodes, id, showSuccess, showError]);

    // Função para atualizar a primeira questão baseada na posição
    const updateFirstQuestionBasedOnPosition = useCallback((updatedNodes) => {
        // Só atualiza se não houver uma primeira questão definida manualmente
        const nonFinalNodes = updatedNodes.filter(node => !node.data.is_final);
        if (nonFinalNodes.length > 0) {
            const topNode = nonFinalNodes.reduce((min, node) => 
                node.position.y < min.position.y ? node : min
            , nonFinalNodes[0]);
            
            // Se não houver primeira questão definida ou se a questão mais ao topo mudou
            if (!flow?.first_question_id || 
                (flow?.first_question_id && 
                 !nonFinalNodes.some(node => 
                    node.id === flow.first_question_id.toString() || 
                    node.data.original_id === flow.first_question_id
                 ))
            ) {
                handleSetAsFirstQuestion(topNode.id);
            }
        }
    }, [flow?.first_question_id, handleSetAsFirstQuestion]);

    // Função para forçar a recarga do fluxo
    const forceReloadFlow = useCallback(async () => {
        try {
            console.log("Forçando recarga do fluxo...");
            const refreshedFlow = await flowService.get(Number(id));
            
            // Preserva o nome do fluxo que pode ter sido perdido na resposta do backend
            if (!refreshedFlow.name && flow?.name) {
                refreshedFlow.name = flow.name;
            }
            
            console.log("Fluxo recarregado:", refreshedFlow);
            setFlow(refreshedFlow);
            
            if (refreshedFlow.questions && refreshedFlow.questions.length > 0) {
                console.log(`Reinicializando nós e arestas com ${refreshedFlow.questions.length} questões`);
                initializeNodesAndEdges(refreshedFlow.questions);
            } else {
                console.warn("Fluxo recarregado não contém questões!");
            }
            
            return refreshedFlow;
        } catch (error) {
            console.error("Erro ao recarregar fluxo:", error);
            showError("Erro ao recarregar o fluxo. Por favor, tente novamente.");
            return null;
        }
    }, [id, flow?.name, initializeNodesAndEdges, showError]);

    // Carregar fluxo
    useEffect(() => {
        const loadFlow = async () => {
            try {
                // Se não tiver id, for undefined ou for NaN, trata como novo fluxo
                const flowId = (!id || id === 'undefined' || id === 'NaN') ? 'new' : id;
                console.log(`Carregando fluxo com ID: ${flowId}`);
                
                const data = await flowService.get(flowId);
                console.log(`Fluxo carregado:`, data);
                
                // Verifica se o nome do fluxo existe na resposta
                if (!data.name && flowId !== 'new') {
                    // Tenta recuperar o nome do localStorage
                    const storedName = localStorage.getItem(`flow_${flowId}_name`);
                    if (storedName) {
                        data.name = storedName;
                    }
                }
                
                setFlow(data);
                if (flowId !== 'new' && data.questions) {
                    console.log(`Inicializando nós e arestas com ${data.questions.length} questões`);
                    
                    // Verifica se as questões têm os campos necessários
                    const validQuestions = data.questions.filter(q => q && q.id && q.text);
                    
                    if (validQuestions.length !== data.questions.length) {
                        console.warn(`Encontradas ${data.questions.length - validQuestions.length} questões inválidas`);
                    }
                    
                    // Garante que todas as questões tenham o campo is_active definido como true
                    const processedQuestions = validQuestions.map(q => ({
                        ...q,
                        is_active: true
                    }));
                    
                    console.log(`Inicializando com ${processedQuestions.length} questões válidas`);
                    initializeNodesAndEdges(processedQuestions);
                }
            } catch (error) {
                console.error('Erro ao carregar fluxo:', error);
                showError('Erro ao carregar o fluxo. Por favor, tente novamente.');
                // Redireciona para a lista em caso de erro
                navigate('/flows-new');
            }
        };
        loadFlow();
    }, [id, showError, navigate, initializeNodesAndEdges]);

    // Handler para desfazer
    const handleUndo = useCallback(() => {
        if (currentHistoryIndex > 0 && history[currentHistoryIndex - 1]) {
            const previousState = history[currentHistoryIndex - 1];
            if (previousState?.nodes && previousState?.edges) {
                setNodes([...previousState.nodes]);
                setEdges([...previousState.edges]);
                setCurrentHistoryIndex(prev => prev - 1);
            }
        }
    }, [currentHistoryIndex, history]);

    // Handler para refazer
    const handleRedo = useCallback(() => {
        if (currentHistoryIndex < history.length - 1 && history[currentHistoryIndex + 1]) {
            const nextState = history[currentHistoryIndex + 1];
            if (nextState?.nodes && nextState?.edges) {
                setNodes([...nextState.nodes]);
                setEdges([...nextState.edges]);
                setCurrentHistoryIndex(prev => prev + 1);
            }
        }
    }, [currentHistoryIndex, history]);

    // Função para adicionar um novo estado ao histórico
    const addToHistory = useCallback((nodes, edges) => {
        if (!nodes || !edges) return;
        
        setHistory(prev => {
            // Remove estados futuros se estiver desfazendo
            const newHistory = prev.slice(0, currentHistoryIndex + 1);
            // Cria cópias profundas dos nós e edges para evitar referências
            const newState = {
                nodes: JSON.parse(JSON.stringify(nodes)),
                edges: JSON.parse(JSON.stringify(edges))
            };
            return [...newHistory, newState];
        });
        setCurrentHistoryIndex(prev => prev + 1);
    }, [currentHistoryIndex]);

    // Listener para atalhos de teclado
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Não executa se estiver em um campo de texto
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
                return;
            }
            
            if (event.ctrlKey && event.key === 'z') {
                event.preventDefault();
                handleUndo();
            } else if (event.ctrlKey && event.key === 'y') {
                event.preventDefault();
                handleRedo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo]);

    // Inicializar histórico quando o fluxo é carregado
    useEffect(() => {
        if (nodes?.length > 0 || edges?.length > 0) {
            // Limpa o histórico anterior
            setHistory([]);
            setCurrentHistoryIndex(-1);
            // Adiciona o estado inicial
            addToHistory(nodes, edges);
        }
    }, [flow?.id]); // Só executa quando o fluxo é carregado inicialmente

    // Modificar handlers existentes para adicionar ao histórico
    const onNodesChange = useCallback(
        (changes) => {
            setNodes((nds) => {
                const updatedNodes = applyNodeChanges(changes, nds);
                
                // Adiciona ao histórico apenas para mudanças significativas
                const significantChange = changes.some(change => 
                    change.type === 'position' || 
                    change.type === 'remove' || 
                    change.type === 'add'
                );
                
                if (significantChange) {
                    addToHistory(updatedNodes, edges);
                }
                
                // Verifica se houve mudança de posição
                const positionChange = changes.find(change => change.type === 'position');
                if (positionChange) {
                    updateFirstQuestionBasedOnPosition(updatedNodes);
                }
                
                // Atualizar nó selecionado
                const selectedChange = changes.find((change) => change.type === 'select');
                if (selectedChange) {
                    const node = updatedNodes.find((n) => n.id === selectedChange.id);
                    setSelectedNode(selectedChange.selected ? node || null : null);
                }
                
                return updatedNodes;
            });
        },
        [edges, updateFirstQuestionBasedOnPosition, addToHistory]
    );

    const onEdgesChange = useCallback(
        (changes) => {
            setEdges((eds) => {
                const updatedEdges = applyEdgeChanges(changes, eds);
                // Adiciona ao histórico para mudanças em edges
                if (changes.some(change => change.type === 'remove' || change.type === 'add')) {
                    addToHistory(nodes, updatedEdges);
                }
                return updatedEdges;
            });
        },
        [nodes, addToHistory]
    );

    // Handlers para mudanças no fluxo
    const onConnect = useCallback(
        (connection) => {
            console.log('Nova conexão:', connection);
            
            const sourceNode = nodes.find((n) => n.id === connection.source);
            const targetNode = nodes.find((n) => n.id === connection.target);
            const sourceQuestion = sourceNode?.data;
            
            if (sourceQuestion && !sourceQuestion.is_final && targetNode) {
                // Usa o ID original do nó de destino, se disponível
                const targetId = targetNode.data.original_id || Number(connection.target);
                console.log(`Usando ID original do nó de destino: ${targetId}`);
                
                const newEdge = {
                    ...connection,
                    id: `${connection.source}-${connection.sourceHandle}-${connection.target}`,
                    label: sourceQuestion.options[Number(connection.sourceHandle)],
                    type: 'smoothstep',
                    animated: true,
                    style: { strokeWidth: 2, zIndex: 9999 }
                };

                console.log('Nova edge a ser criada:', newEdge);

                // Filtra as edges existentes para remover apenas a edge que sai da mesma opção
                const filteredEdges = edges.filter(edge => 
                    !(edge.source === connection.source && 
                      edge.sourceHandle === connection.sourceHandle)
                );

                console.log('Edges após filtro:', filteredEdges);

                const updatedEdges = [...filteredEdges, newEdge];
                console.log('Edges finais:', updatedEdges);
                setEdges(updatedEdges);
                
                // Atualiza o next_question_logic da questão de origem
                const updatedNodes = nodes.map((node) => {
                    if (node.id === connection.source) {
                        // Cria uma cópia do next_question_logic atual
                        const updatedLogic = { ...node.data.next_question_logic };
                        
                        // Atualiza apenas a opção específica que foi conectada
                        updatedLogic[connection.sourceHandle || '0'] = targetId;
                        
                        console.log('Atualizando next_question_logic:', {
                            nodeId: node.id,
                            opção: connection.sourceHandle,
                            destino: targetId,
                            novaLógica: updatedLogic
                        });
                        
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                next_question_logic: updatedLogic,
                            },
                        };
                    }
                    return node;
                });
                setNodes(updatedNodes);

                // Atualiza o fluxo no backend imediatamente
                const updatedFlow = {
                    ...flow,
                    questions: updatedNodes.map(node => ({
                        ...node.data,
                        position_x: node.position.x,
                        position_y: node.position.y
                    }))
                };

                // Adiciona ao histórico antes de salvar
                addToHistory(updatedNodes, updatedEdges);

                flowService.update(Number(id), updatedFlow)
                    .then(() => {
                        showSuccess('Conexão criada com sucesso!');
                    })
                    .catch(error => {
                        console.error('Erro ao criar conexão:', error);
                        showError('Erro ao criar a conexão. Por favor, tente novamente.');
                        
                        // Em caso de erro, reverte as mudanças locais
                        setNodes(nodes);
                        setEdges(edges);
                    });
            }
        },
        [nodes, edges, flow, id, showSuccess, showError, addToHistory]
    );

    // Handlers para questões
    const onNodeClick = useCallback((event, node) => {
        event.stopPropagation();
        event.preventDefault();
        
        if (!event.ctrlKey && !event.shiftKey) {
        setSelectedNode(node);
            setSelectedNodes(new Set([node.id]));
            setSelectedEdge(null);
            
            // Abre o modal apropriado imediatamente
            if (node.data.is_final) {
                setIsNewQuestion(false);
                setIsConclusionFormOpen(true);
            } else {
                setIsNewQuestion(false);
                setIsQuestionFormOpen(true);
            }
        }
    }, []);

    const handleAddQuestion = (type) => {
        setIsNewQuestion(true);
        setSelectedNode(null);
        setSelectedEdge(null);
        if (type === 'question') {
            setIsQuestionFormOpen(true);
        } else {
            setIsConclusionFormOpen(true);
        }
    };

    // Funções de validação
    const validateFlow = () => {
        const warnings = [];
        const errors = [];

        // Validar se há questões
        if (!nodes.length) {
            errors.push('O fluxo precisa ter pelo menos uma questão');
        }

        // Validar questões não-finais
        nodes.forEach((node) => {
            const question = node.data;
            if (!question.is_final) {
                // Validar se tem opções
                if (!question.options?.length) {
                    warnings.push(`A questão "${question.text}" não possui opções definidas`);
                }

                // Validar se todas as opções estão conectadas
                const nodeEdges = edges.filter(edge => edge.source === node.id);
                question.options?.forEach((option, index) => {
                    const hasConnection = nodeEdges.some(edge => edge.sourceHandle === index.toString());
                    if (!hasConnection) {
                        warnings.push(`A opção "${option}" da questão "${question.text}" não está conectada a outra questão`);
                    }
                });
            }
        });

        return {
            isValid: errors.length === 0,
            canSaveAsDraft: warnings.length > 0 && errors.length === 0,
            errors,
            warnings
        };
    };

    // Salvar fluxo
    const handleSave = async (saveAsDraft = false) => {
        if (!flow) return;

        // Validar fluxo
        const validation = validateFlow();
        
        if (!validation.isValid) {
            // Se houver erros críticos, não permite salvar nem como rascunho
            if (validation.errors.length > 0) {
            validation.errors.forEach(error => showError(error));
            return;
            }
            
            // Se houver apenas warnings e não estiver salvando como rascunho,
            // pergunta se deseja salvar como rascunho
            if (validation.warnings.length > 0 && !saveAsDraft) {
                if (window.confirm('O fluxo possui questões incompletas. Deseja salvar como rascunho?')) {
                    setIsDraft(true);
                } else {
                    validation.warnings.forEach(warning => showWarning(warning));
                    return;
                }
            }
        }

        try {
            console.log("Iniciando processo de salvamento do fluxo");
            setLoading(true);
            
            // Prepara os dados do fluxo para salvar
            const flowData = {
                ...flow,
                is_draft: saveAsDraft || isDraft,
                questions: nodes.map(node => ({
                    ...node.data,
                    position_x: node.position.x,
                    position_y: node.position.y,
                    is_active: true
                }))
            };
            
            console.log("Dados do fluxo preparados para salvamento:", flowData);
            
            // Salva o fluxo
            let savedFlow;
            if (flowData.id === 0 || flowData.id === '0') {
                console.log("Criando novo fluxo...");
                savedFlow = await flowService.create(flowData);
                // Atualiza o ID na URL
                navigate(`/flows-new/${savedFlow.id}/edit`);
                    } else {
                console.log(`Atualizando fluxo existente (ID: ${flowData.id})...`);
                savedFlow = await flowService.update(Number(id), flowData);
            }
            
            console.log("Fluxo salvo com sucesso:", savedFlow);
            showSuccess('Fluxo salvo com sucesso!');
            
            // Verifica se todas as questões foram salvas corretamente
            if (savedFlow.questions) {
                const sentQuestionIds = new Set(flowData.questions.map(q => q.id.toString()));
                const receivedQuestionIds = new Set(savedFlow.questions.map(q => q.id.toString()));
                
                console.log('IDs de questões enviadas:', Array.from(sentQuestionIds));
                console.log('IDs de questões recebidas:', Array.from(receivedQuestionIds));
                
                // Verifica se há questões faltando
                const missingQuestions = [];
                sentQuestionIds.forEach(id => {
                    if (!receivedQuestionIds.has(id)) {
                        missingQuestions.push(id);
                    }
                });
                
                if (missingQuestions.length > 0) {
                    console.warn('Questões faltando na resposta:', missingQuestions);
                    
                    // Força a recarga do fluxo para garantir que todas as questões sejam carregadas
                    await forceReloadFlow();
            } else {
                    // Atualiza o estado com o fluxo salvo
                    setFlow(savedFlow);
                    initializeNodesAndEdges(savedFlow.questions);
                }
            } else {
                console.warn("Resposta do backend não contém questões!");
                
                // Recarrega o fluxo para garantir que as conexões estejam corretas
                await forceReloadFlow();
            }
        } catch (error) {
            console.error('Erro ao salvar fluxo:', error);
            showError('Erro ao salvar o fluxo. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Salvar questão
    const handleSaveQuestion = async (questionData) => {
        try {
            setLoading(true);
            console.log("Dados da questão para salvar:", questionData);
            
            // Validação básica
            if (!questionData.text?.trim()) {
                showError('O título da questão é obrigatório');
                setLoading(false);
                return;
            }
            
            if (questionData.is_final && !questionData.final_message?.trim()) {
                showError('A mensagem final é obrigatória para conclusões');
                setLoading(false);
                return;
            }
            
            // Se for uma nova questão
            if (!questionData.id) {
                console.log("Criando nova questão...");
                
                // Obtém a referência do wrapper do ReactFlow
                const flowWrapper = document.querySelector('.react-flow__viewport');
                const { x: viewportX, y: viewportY, zoom } = flowWrapper ? 
                    flowWrapper.style.transform.match(/translate\((.*?)px, (.*?)px\) scale\((.*?)\)/).slice(1).map(Number) : 
                    [0, 0, 1];
                
                // Obtém as dimensões do container visível
                const container = document.querySelector('.react-flow-container');
                const containerRect = container?.getBoundingClientRect();
                const containerWidth = containerRect?.width || 1000;
                const containerHeight = containerRect?.height || 800;
                
                // Calcula o centro da viewport atual
                const position = {
                    x: (-viewportX + containerWidth / 2) / zoom,
                    y: (-viewportY + containerHeight / 2) / zoom
                };
                
                // Gera um ID único para a nova questão
                const newId = Date.now().toString();
                
                // Prepara os dados da questão
                const newQuestionData = {
                    ...questionData,
                    id: newId,
                    original_id: newId,
                    flow_id: flow.id,
                    next_question_logic: {},
                    position_x: position.x,
                    position_y: position.y
                };
                
                // Cria o novo nó
                const newNode = {
                    id: newId,
                    type: 'question',
                    position: position,
                    data: {
                        ...newQuestionData,
                        flow_first_question_id: flow.first_question_id,
                        onSetAsFirstQuestion: handleSetAsFirstQuestion,
                        onDelete: handleDeleteQuestion
                    },
                };
                
                try {
                    // Primeiro atualiza o estado local para manter a UI responsiva
                    setNodes(prevNodes => [...prevNodes, newNode]);
                
                    // Atualiza o fluxo preservando o next_question_logic e posições existentes
                const updatedFlow = {
                    ...flow,
                        questions: flow.questions.map(q => {
                            // Encontra o nó correspondente para pegar o next_question_logic e posição atuais
                            const node = nodes.find(n => n.id === q.id.toString());
                            return {
                                ...q,
                                next_question_logic: node?.data?.next_question_logic || q.next_question_logic,
                                position_x: node?.position?.x || q.position_x,
                                position_y: node?.position?.y || q.position_y
                            };
                        }).concat([newQuestionData])
                    };
                    
                    // Salva no backend
                    const savedFlow = await flowService.update(Number(id), updatedFlow);
                    
                    // Se o backend retornou um ID diferente para a nova questão, atualiza o mapeamento
                    const savedQuestion = savedFlow.questions.find(q => 
                        q.text === newQuestionData.text && q.id !== newQuestionData.id
                    );
                    
                    if (savedQuestion) {
                        // Atualiza o nó com o novo ID mantendo as conexões
                        setNodes(prevNodes => prevNodes.map(node => {
                            if (node.id === newId) {
                                return {
                                    ...node,
                                    id: savedQuestion.id.toString(),
                                    data: {
                                        ...node.data,
                                        id: savedQuestion.id,
                                        original_id: savedQuestion.id
                                    }
                                };
                            }
                            return node;
                        }));
                        
                        // Atualiza as edges com o novo ID
                        setEdges(prevEdges => prevEdges.map(edge => ({
                            ...edge,
                            source: edge.source === newId ? savedQuestion.id.toString() : edge.source,
                            target: edge.target === newId ? savedQuestion.id.toString() : edge.target
                        })));
                    }
                    
                    showSuccess('Questão adicionada com sucesso!');
                } catch (error) {
                    console.error('Erro ao salvar nova questão:', error);
                    showError('Erro ao salvar a questão. Por favor, tente novamente.');
                    
                    // Reverte as mudanças locais em caso de erro
                    setNodes(prevNodes => prevNodes.filter(node => node.id !== newId));
                    return;
                }
            } else {
                // Se for uma questão existente
                try {
                    console.log("Atualizando questão existente:", questionData.id);
                    
                    try {
                        // Atualizar a questão no banco
                        const response = await api.put(`/question-bank/${questionData.id}`, questionData);
                        
                        // Atualizar a questão no fluxo
                        const flowResponse = await api.put(`/question-bank/flow/${flow.id}/question/${selectedNode.data.flow_question_id}`, {
                            // Dados para atualizar a configuração da questão no fluxo
                            // Mantenha as coordenadas e outras informações específicas do fluxo
                            position: selectedNode.data.position,
                            style: selectedNode.data.style
                        });
                        
                        // Atualizar o nó na UI
                        setNodes(prevNodes => 
                            prevNodes.map(node => {
                                if (node.id === selectedNode.id) {
                                    // Criar uma cópia profunda para garantir a atualização da UI
                                    const updatedData = JSON.parse(JSON.stringify({
                                        ...node.data,
                                        text: questionData.text,
                                        options: questionData.options,
                                        next_question_logic: questionData.next_question_logic,
                                        is_final: questionData.is_final,
                                        article_reference: questionData.article_reference,
                                        dynamic_text: questionData.dynamic_text,
                                        previous_question: questionData.previous_question,
                                        final_message: questionData.final_message,
                                        is_success: questionData.is_success
                                    }));
                                    
                                    return {
                                        ...node,
                                        data: updatedData
                                    };
                                }
                                return node;
                            })
                        );
                        
                        showSuccess("Questão atualizada com sucesso!");
                        // Fechar o modal
                        setIsQuestionFormOpen(false);
                        
                        // Preservar a seleção do nó para feedback visual
                        setTimeout(() => {
                            // Adicione a seleção novamente para feedback visual
                            setSelectedNodes(new Set([selectedNode.id]));
                        }, 50);
                        
                    } catch (error) {
                        console.error("Erro ao atualizar questão:", error);
                        showError(`Erro ao atualizar questão: ${error.message || 'Erro desconhecido'}`);
                    }
                } catch (error) {
                    console.error('Erro ao salvar questão:', error);
                    showError(`Erro ao salvar questão: ${error.message || 'Erro desconhecido'}`);
                }
            }
        } catch (error) {
            console.error('Erro ao salvar questão:', error);
            showError(`Erro ao salvar questão: ${error.message || 'Erro desconhecido'}`);
        } finally {
            setLoading(false);
        }
    };

    // Adicionar novo handler para mudanças de posição
    const onNodeDragStop = useCallback((event, node) => {
        // Atualiza a posição do nó no estado local
        setNodes(prevNodes => prevNodes.map(n => {
            if (n.id === node.id) {
                return {
                    ...n,
                    position: node.position,
                    data: {
                        ...n.data,
                        position_x: node.position.x,
                        position_y: node.position.y
                    }
                };
            }
            return n;
        }));

        // Atualiza o fluxo no backend com as novas posições
        const updatedFlow = {
            ...flow,
            questions: flow.questions.map(q => {
                if (q.id.toString() === node.id) {
                    return {
                        ...q,
                        position_x: node.position.x,
                        position_y: node.position.y,
                        original_id: Number(q.original_id) || Number(q.id) // Garante que original_id seja numérico
                    };
                }
                return {
                    ...q,
                    original_id: Number(q.original_id) || Number(q.id) // Garante que original_id seja numérico
                };
            })
        };

        // Salva silenciosamente no backend
        flowService.update(Number(id), updatedFlow).catch(error => {
            console.error('Erro ao atualizar posição:', error);
        });
    }, [flow, id]);

    const handleCloneNode = useCallback((nodeToClone) => {
        try {
            // Gera um novo ID numérico baseado no timestamp
            const newId = Date.now();
            
            // Calcula a nova posição (deslocada para a direita e um pouco para baixo)
            const newPosition = {
                x: nodeToClone.position.x + 50,
                y: nodeToClone.position.y + 50
            };
            
            // Cria o novo nó como uma cópia do original
            const newNode = {
                id: newId.toString(),
                type: 'question',
                position: newPosition,
                draggable: true,
                data: {
                    ...nodeToClone.data,
                    id: newId,
                    original_id: newId,
                    text: `${nodeToClone.data.text} (Cópia)`,
                    next_question_logic: {},
                    flow_first_question_id: flow.first_question_id,
                    onSetAsFirstQuestion: handleSetAsFirstQuestion,
                    onDelete: handleDeleteQuestion,
                    position_x: newPosition.x,
                    position_y: newPosition.y
                }
            };

            // Atualiza o fluxo no backend primeiro
            const updatedFlow = {
                ...flow,
                questions: [
                    ...flow.questions.map(q => ({
                        ...q,
                        position_x: nodes.find(n => n.id === q.id.toString())?.position?.x || q.position_x,
                        position_y: nodes.find(n => n.id === q.id.toString())?.position?.y || q.position_y
                    })),
                    {
                        ...nodeToClone.data,
                        id: newId,
                        original_id: newId,
                        text: `${nodeToClone.data.text} (Cópia)`,
                        position_x: newPosition.x,
                        position_y: newPosition.y,
                        next_question_logic: {},
                    }
                ]
            };

            flowService.update(Number(id), updatedFlow)
                .then(() => {
                    // Só adiciona o nó ao estado local após sucesso no backend
                    setNodes(prevNodes => [...prevNodes, newNode]);
                    showSuccess('Questão clonada com sucesso!');
                })
                .catch(error => {
                    console.error('Erro ao clonar questão:', error);
                    showError('Erro ao clonar a questão. Por favor, tente novamente.');
                });
            
        } catch (error) {
            console.error('Erro ao clonar questão:', error);
            showError('Erro ao clonar a questão. Por favor, tente novamente.');
        }
    }, [flow, id, nodes, handleSetAsFirstQuestion, handleDeleteQuestion, showSuccess, showError]);

    // Adicionar função para clonar múltiplos nós
    const handleCloneMultipleNodes = useCallback(() => {
        try {
            // Obtém todos os nós selecionados
            const nodesToClone = nodes.filter(node => selectedNodes.has(node.id));
            if (nodesToClone.length === 0) return;

            // Calcula o centro dos nós selecionados
            const center = nodesToClone.reduce(
                (acc, node) => ({
                    x: acc.x + node.position.x,
                    y: acc.y + node.position.y
                }),
                { x: 0, y: 0 }
            );
            center.x /= nodesToClone.length;
            center.y /= nodesToClone.length;

            // Offset para os novos nós
            const offset = { x: 100, y: 100 };

            // Mapa de IDs antigos para novos
            const idMapping = {};
            const timestamp = Date.now();
            
            // Criar novos nós
            const newNodes = nodesToClone.map((node, index) => {
                const newId = timestamp + index;
                idMapping[node.id] = newId.toString();

                // Preserva o next_question_logic original, mas atualiza os IDs
                const originalNextQuestionLogic = { ...node.data.next_question_logic };
                const updatedNextQuestionLogic = {};
                
                // Atualiza os IDs no next_question_logic apenas se o nó de destino também está sendo clonado
                Object.entries(originalNextQuestionLogic).forEach(([key, targetId]) => {
                    const targetNode = nodesToClone.find(n => n.id === targetId?.toString());
                    if (targetNode) {
                        const newTargetId = timestamp + nodesToClone.indexOf(targetNode);
                        updatedNextQuestionLogic[key] = newTargetId;
                    }
                });

                return {
                    id: newId.toString(),
                    type: 'question',
                    position: {
                        x: node.position.x - center.x + center.x + offset.x,
                        y: node.position.y - center.y + center.y + offset.y
                    },
                    draggable: true,
                    selected: true,
                    data: {
                        ...node.data,
                        id: newId,
                        original_id: newId,
                        text: `${node.data.text} (Cópia)`,
                        next_question_logic: updatedNextQuestionLogic,
                        flow_first_question_id: flow.first_question_id,
                        onSetAsFirstQuestion: handleSetAsFirstQuestion,
                        onDelete: handleDeleteQuestion,
                        position_x: node.position.x - center.x + center.x + offset.x,
                        position_y: node.position.y - center.y + center.y + offset.y
                    }
                };
            });

            // Encontrar todas as edges entre os nós selecionados
            const relevantEdges = edges.filter(edge => 
                selectedNodes.has(edge.source) && selectedNodes.has(edge.target)
            );

            // Criar novas edges entre os nós clonados
            const newEdges = relevantEdges.map(edge => {
                const newSourceId = idMapping[edge.source];
                const newTargetId = idMapping[edge.target];
                
                if (newSourceId && newTargetId) {
                    return {
                        ...edge,
                        id: `${newSourceId}-${edge.sourceHandle}-${newTargetId}`,
                        source: newSourceId,
                        target: newTargetId,
                        animated: true,
                        label: edge.label,
                        style: { strokeWidth: 2, zIndex: 999999 }
                    };
                }
                return null;
            }).filter(Boolean);

            // Atualizar o fluxo no backend
            const updatedFlow = {
                ...flow,
                questions: [
                    ...flow.questions.map(q => ({
                        ...q,
                        position_x: nodes.find(n => n.id === q.id.toString())?.position?.x || q.position_x,
                        position_y: nodes.find(n => n.id === q.id.toString())?.position?.y || q.position_y
                    })),
                    ...newNodes.map(node => ({
                        ...node.data,
                        position_x: node.position.x,
                        position_y: node.position.y
                    }))
                ]
            };

            flowService.update(Number(id), updatedFlow)
                .then(() => {
                    // Desseleciona os nós originais
                    const updatedOriginalNodes = nodes.map(node => ({
                        ...node,
                        selected: false
                    }));

                    // Adiciona os novos nós já selecionados
                    setNodes([...updatedOriginalNodes, ...newNodes]);
                    setEdges(prevEdges => [...prevEdges, ...newEdges]);
                    
                    // Atualiza a seleção para incluir apenas os novos nós
                    const newSelectedNodes = new Set(newNodes.map(node => node.id));
                    setSelectedNodes(newSelectedNodes);
                    
                    // Adiciona ao histórico
                    addToHistory([...updatedOriginalNodes, ...newNodes], [...edges, ...newEdges]);
                    
                    showSuccess('Questões clonadas com sucesso!');
                })
                .catch(error => {
                    console.error('Erro ao clonar questões:', error);
                    showError('Erro ao clonar as questões. Por favor, tente novamente.');
                });
        } catch (error) {
            console.error('Erro ao clonar questões:', error);
            showError('Erro ao clonar as questões. Por favor, tente novamente.');
        }
    }, [nodes, edges, selectedNodes, flow, id, handleSetAsFirstQuestion, handleDeleteQuestion, showSuccess, showError, addToHistory]);

    // Modificar o handler de seleção de nós
    const onSelectionChange = useCallback(({ nodes: selectedNodes }) => {
        setSelectedNodes(new Set(selectedNodes.map(node => node.id)));
        if (selectedNodes.length === 1) {
            setSelectedNode(selectedNodes[0]);
        } else {
            setSelectedNode(null);
        }
        setSelectedEdge(null);
    }, []);

    // Função para alternar o modo fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            // Entrar em modo fullscreen
            const element = document.querySelector('.react-flow-container');
            if (element?.requestFullscreen) {
                element.requestFullscreen();
            } else if (element?.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element?.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            setIsFullscreen(true);
            
            // Fechar modais abertos para evitar problemas de renderização
            if (isQuestionFormOpen || isConclusionFormOpen) {
                setIsQuestionFormOpen(false);
                setIsConclusionFormOpen(false);
                // Reabriremos o modal após entrar em tela cheia
                setTimeout(() => {
                    if (selectedNode) {
                        if (selectedNode.data.is_final === true) {
                            setIsConclusionFormOpen(true);
                        } else {
                            setIsQuestionFormOpen(true);
                        }
                    }
                }, 300); // Pequeno atraso para garantir que o modo de tela cheia esteja ativo
            }
        } else {
            // Sair do modo fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
            
            // Fechar modais abertos para evitar problemas de renderização
            if (isQuestionFormOpen || isConclusionFormOpen) {
                setIsQuestionFormOpen(false);
                setIsConclusionFormOpen(false);
                // Reabriremos o modal após sair da tela cheia
                setTimeout(() => {
                    if (selectedNode) {
                        if (selectedNode.data.is_final === true) {
                            setIsConclusionFormOpen(true);
                        } else {
                            setIsQuestionFormOpen(true);
                        }
                    }
                }, 300); // Pequeno atraso para garantir que o modo de tela cheia esteja desativado
            }
        }
    };

    // Adicionar listener para mudanças no estado do fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Adicionar handler para excluir edge
    const handleDeleteEdge = useCallback((edgeId) => {
        try {
            // Encontra a edge que será excluída
            const edgeToDelete = edges.find(edge => edge.id === edgeId);
            if (!edgeToDelete) return;

            // Encontra o nó de origem
            const sourceNode = nodes.find(node => node.id === edgeToDelete.source);
            if (!sourceNode) return;

            // Remove a conexão do next_question_logic
            const updatedNodes = nodes.map(node => {
                if (node.id === edgeToDelete.source) {
                    const updatedLogic = { ...node.data.next_question_logic };
                    delete updatedLogic[edgeToDelete.sourceHandle];
                    
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            next_question_logic: updatedLogic
                        }
                    };
                }
                return node;
            });

            // Atualiza o estado local
            setNodes(updatedNodes);
            setEdges(edges.filter(edge => edge.id !== edgeId));
            setSelectedEdge(null);

            // Atualiza o backend
            const updatedFlow = {
                ...flow,
                questions: updatedNodes.map(node => ({
                    ...node.data,
                    position_x: node.position.x,
                    position_y: node.position.y
                }))
            };

            flowService.update(Number(id), updatedFlow)
                .then(() => {
                    showSuccess('Conexão removida com sucesso!');
                })
                .catch(error => {
                    console.error('Erro ao remover conexão:', error);
                    showError('Erro ao remover a conexão. Por favor, tente novamente.');
                });

        } catch (error) {
            console.error('Erro ao excluir conexão:', error);
            showError('Erro ao excluir a conexão. Por favor, tente novamente.');
        }
    }, [edges, nodes, flow, id, showSuccess, showError]);

    if (!flow) return null;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <GlobalStyle />
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" component="h1" sx={{ fontSize: '1.5rem' }}>
                            {flow?.name || 'Novo Fluxo'}
                </Typography>
                        {isDraft && (
                            <Typography
                                variant="caption"
                                sx={{
                                    backgroundColor: theme.palette.warning.main,
                                    color: theme.palette.warning.contrastText,
                                    px: 1.5,
                                    py: 0.7,
                                    borderRadius: 1,
                                    fontWeight: 'medium',
                                    fontSize: '0.95rem'
                                }}
                            >
                                RASCUNHO
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Tooltip title="Recarregar Fluxo">
                            <IconButton 
                                color="primary" 
                                onClick={forceReloadFlow}
                                disabled={loading}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isFullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}>
                            <IconButton
                                color="primary"
                                onClick={toggleFullscreen}
                            >
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSave(false)}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                        Salvar
                    </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => handleSave(true)}
                            disabled={loading}
                        >
                            Salvar como Rascunho
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/flows-new')}
                            disabled={loading}
                        >
                            Voltar
                        </Button>
                    </Stack>
                </Box>
                <Paper 
                    sx={{ 
                        height: 'calc(100vh - 200px)', 
                        position: 'relative',
                        ...(isFullscreen && {
                            height: '100vh',
                            width: '100vw',
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            zIndex: 9999,
                            m: 0,
                            borderRadius: 0
                        })
                    }}
                    className={`react-flow-container ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}
                >
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    nodeTypes={nodeTypes}
                    fitView
                    minZoom={0.1}
                    maxZoom={4}
                    defaultZoom={1.5}
                    onNodeDragStop={onNodeDragStop}
                    connectionLineStyle={{ stroke: theme.palette.primary.main, strokeWidth: 2, zIndex: 999999 }}
                    defaultEdgeOptions={{
                            style: { stroke: theme.palette.primary.main, strokeWidth: 2, zIndex: 999999 },
                        animated: true,
                            type: 'smoothstep'
                        }}
                        edgesFocusable={true}
                        elementsSelectable={true}
                        multiSelectionKeyCode="Control"
                        selectionKeyCode="Shift"
                        selectNodesOnDrag={false}
                        onSelectionChange={onSelectionChange}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        panOnScroll={true}
                        panOnDrag={true}
                        className="react-flow-container"
                        style={{ 
                            zIndex: 0,
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? '#121212' // Fundo mais escuro no modo escuro
                                : theme.palette.background.default
                        }}
                        onEdgeClick={(event, edge) => {
                            event.stopPropagation(); // Previne propagação do evento
                            setSelectedEdge(edge);
                            setSelectedNode(null);
                            setSelectedNodes(new Set());
                        }}
                        onNodeClick={(event, node) => {
                            event.stopPropagation(); // Previne propagação do evento
                            if (!event.ctrlKey && !event.shiftKey) {
                                setSelectedNode(node);
                                setSelectedNodes(new Set([node.id]));
                                setSelectedEdge(null);
                            }
                        }}
                        onPaneClick={(event) => {
                            // Verifica se o clique foi realmente no painel
                            const isClickOnPane = event.target.classList.contains('react-flow__pane');
                            
                            // Verifica se o clique foi em elementos que não devem fechar o modal
                            const isClickOnSpeedDial = event.target.closest('.MuiSpeedDial-root') !== null;
                            const isClickOnModal = event.target.closest('.MuiDialog-root') !== null;
                            const isClickOnEditButton = event.target.closest('[data-edit-button="true"]') !== null;
                            const isClickOnPaper = event.target.closest('.MuiPaper-root') !== null;
                            
                            // Só limpa a seleção e fecha os modais se o clique for no painel
                            // e não em outros elementos importantes
                            if (isClickOnPane && 
                                !isClickOnSpeedDial && 
                                !isClickOnModal && 
                                !isClickOnEditButton && 
                                !isClickOnPaper) {
                                setSelectedNode(null);
                                setSelectedEdge(null);
                                setSelectedNodes(new Set());
                                setIsQuestionFormOpen(false);
                                setIsConclusionFormOpen(false);
                            }
                    }}
                >
                    <Background 
                        color={theme.palette.mode === 'dark' ? '#333333' : '#aaaaaa'}
                        gap={16}
                        size={1}
                        variant={theme.palette.mode === 'dark' ? 'dots' : 'dots'}
                    />
                        <Controls 
                            showZoom={true}
                            showFitView={true}
                            showInteractive={true}
                        />
                        <MiniMap 
                            nodeColor={getNodeColor}
                            nodeStrokeWidth={3}
                            zoomable
                            pannable
                            style={{
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.background.paper, 0.7)
                                    : alpha(theme.palette.background.paper, 0.9),
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                borderRadius: 4,
                                boxShadow: theme.shadows[4]
                            }}
                        />

                        {/* SpeedDial para adicionar questões */}
                        <div style={{ 
                            position: 'absolute',
                            bottom: 200,
                            right: 20,
                            zIndex: 999999
                        }}>
            <SpeedDial
                ariaLabel="Adicionar questão"
                icon={<SpeedDialIcon />}
                direction="up"
                sx={{
                    '& .MuiFab-primary': {
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                        boxShadow: theme.shadows[4]
                    }
                }}
            >
                <SpeedDialAction
                    icon={<QuestionIcon />}
                    tooltipTitle="Adicionar Questão"
                    onClick={() => handleAddQuestion('question')}
                />
                <SpeedDialAction
                    icon={<CheckCircleIcon />}
                    tooltipTitle="Adicionar Conclusão"
                    onClick={() => handleAddQuestion('conclusion')}
                />
            </SpeedDial>
                        </div>
                        {selectedNodes.size > 0 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 999999,
                                    display: 'flex',
                                    gap: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    padding: 1,
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[4]
                                }}
                            >
                                {selectedNodes.size === 1 && selectedNode && (
                                    <>
                                        <Tooltip title="Definir como Primeira Questão">
                                            <IconButton
                                                onClick={() => handleSetAsFirstQuestion(selectedNode.id)}
                                                sx={{
                                                    backgroundColor: selectedNode.data.flow_first_question_id === selectedNode.id
                                                        ? theme.palette.primary.main
                                                        : alpha(theme.palette.background.paper, 0.9),
                                                    color: selectedNode.data.flow_first_question_id === selectedNode.id
                                                        ? theme.palette.primary.contrastText
                                                        : theme.palette.primary.main,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: theme.palette.primary.contrastText
                                                    }
                                                }}
                                            >
                                                <PlayCircleOutlineIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Editar">
                                            <IconButton
                                                data-edit-button="true"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    // Garantir que o isNewQuestion seja false, já que estamos editando uma questão existente
                                                    setIsNewQuestion(false);
                                                    
                                                    console.log("Abrindo modal para editar:", selectedNode.data);
                                                    
                                                    // Garantir que o selectedNode esteja definido corretamente
                                                    if (selectedNode) {
                                                        // Verificar se é uma conclusão baseado na propriedade is_final
                                                        if (selectedNode.data.is_final === true) {
                                                            console.log("Abrindo modal de conclusão para editar:", selectedNode.data);
                                                            setIsConclusionFormOpen(true);
                                                        } else {
                                                            console.log("Abrindo modal de questão para editar:", selectedNode.data);
                                                            // Pequeno atraso para garantir que o estado seja atualizado corretamente
                                                            setTimeout(() => {
                                                                setIsQuestionFormOpen(true);
                                                            }, 10);
                                                        }
                                                    }
                                                }}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.dark
                                                    }
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}

                                <Tooltip title={`Clonar ${selectedNodes.size > 1 ? 'Selecionados' : ''}`}>
                                    <IconButton
                                        onClick={() => {
                                            if (selectedNodes.size === 1 && selectedNode) {
                                                handleCloneNode(selectedNode);
                                            } else {
                                                handleCloneMultipleNodes();
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.info.main,
                                            color: theme.palette.info.contrastText,
                                            '&:hover': {
                                                backgroundColor: theme.palette.info.dark
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={`Excluir ${selectedNodes.size > 1 ? 'Selecionados' : ''}`}>
                                    <IconButton
                                        onClick={() => {
                                            if (window.confirm(`Tem certeza que deseja excluir ${selectedNodes.size > 1 ? 'os itens selecionados' : 'este item'}?`)) {
                                                const nodesToDelete = nodes.filter(node => selectedNodes.has(node.id));
                                                nodesToDelete.forEach(node => handleDeleteQuestion(node.id));
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.error.main,
                                            color: theme.palette.error.contrastText,
                                            '&:hover': {
                                                backgroundColor: theme.palette.error.dark
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Botão para excluir conexão selecionada */}
                        {selectedEdge && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    right: selectedNodes.size > 0 ? 'auto' : 10,
                                    left: selectedNodes.size > 0 ? 10 : 'auto',
                                    zIndex: 999999,
                                    display: 'flex',
                                    gap: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                    padding: 1,
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[4]
                                }}
                            >
                                <Tooltip title="Excluir Conexão">
                                    <IconButton
                                        onClick={() => {
                                            if (window.confirm('Tem certeza que deseja excluir esta conexão?')) {
                                                handleDeleteEdge(selectedEdge.id);
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.error.main,
                                            color: theme.palette.error.contrastText,
                                            '&:hover': {
                                                backgroundColor: theme.palette.error.dark
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                </ReactFlow>
                </Paper>
            </Paper>

            {/* Modal de edição de questão */}
            {isQuestionFormOpen && selectedNode?.data && (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 9999999 }}
                    className="modal-wrapper"
                >
                    <QuestionForm
                        open={true}
                        onClose={(event) => {
                            if (event) {
                                event.stopPropagation();
                                event.preventDefault();
                            }
                            setIsQuestionFormOpen(false);
                            // Não zeramos o selectedNode aqui para manter a seleção visual
                        }}
                        onSave={(questionData) => {
                            console.log("Salvando questão:", questionData);
                            handleSaveQuestion(questionData);
                        }}
                        question={{
                            id: selectedNode.data.id,
                            text: selectedNode.data.text || '',
                            options: selectedNode.data.options || [],
                            is_final: selectedNode.data.is_final === true,
                            article_reference: selectedNode.data.article_reference || '',
                            dynamic_text: selectedNode.data.dynamic_text || {},
                            previous_question: selectedNode.data.previous_question || null,
                            next_question_logic: selectedNode.data.next_question_logic || {},
                            final_message: selectedNode.data.final_message || '',
                            is_success: selectedNode.data.is_success
                        }}
                        isNew={isNewQuestion}
                    />
                </div>
            )}

            {/* Modal de edição de conclusão */}
            {isConclusionFormOpen && selectedNode?.data && (
                <div 
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 9999999 }}
                    className="modal-wrapper"
                >
                    <ConclusionForm
                        open={true}
                        onClose={(event) => {
                            if (event) {
                                event.stopPropagation();
                                event.preventDefault();
                            }
                            setIsConclusionFormOpen(false);
                            setSelectedNode(null);
                        }}
                        onSave={handleSaveQuestion}
                        question={selectedNode.data}
                        isNew={isNewQuestion}
                    />
                </div>
            )}
        </Container>
    );
}; 